import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import DepositNoteModal from "../components/DepositNoteModal";
import CreateTableModal from "../components/CreateTableModal";
import TableCard from "../components/TableCard";
import usePaymentReturn from "../hooks/usePaymentReturn";
import { useContent } from "../lib/content";
import { confirmDeleteTable } from "../lib/tables";

function TablesHeader({ onCreate }) {
  const { c } = useContent();
  return (
    <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-10 gap-4">
      <div>
        <div className="label-mono text-neon mb-2">{c.tables_label}</div>
        <h1 className="font-display text-4xl lg:text-6xl whitespace-pre-line">{c.tables_title}</h1>
        <p className="mt-4 text-neutral-400 max-w-xl">{c.tables_subtitle} <span className="text-alert">{c.tables_subtitle_highlight}</span></p>
      </div>
      <button onClick={onCreate} className="btn-primary flex items-center gap-2" data-testid="create-table-btn"><Plus className="w-4 h-4"/> CREA TAVOLO</button>
    </div>
  );
}

export default function Tables() {
  const [tables, setTables] = useState([]);
  const [events, setEvents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [paying, setPaying] = useState(null);
  const [editing, setEditing] = useState(null);
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    const [t, e] = await Promise.all([api.get("/tables"), api.get("/events")]);
    setTables(t.data); setEvents(e.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  usePaymentReturn(params, load);

  const requireUser = (fn) => (arg) => { if (loading) return; if (!user) { nav("/login"); return; } fn(arg); };
  const joinTable = requireUser(async (t) => {
    try {
      await api.post(`/tables/${t.id}/join`, {});
      toast.success("Entrato nel tavolo. Ora blocca il posto con la caparra.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  });

  const deleteTable = async (t) => { if (await confirmDeleteTable(t)) load(); };

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16">
      <TablesHeader onCreate={requireUser(() => setShowCreate(true))}/>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {tables.length === 0 && (
          <div className="col-span-full border border-neutral-800 p-12 text-center">
            <div className="label-mono text-neutral-500">Nessun tavolo aperto. Sii il primo.</div>
          </div>
        )}
        {tables.map((t, i) => <TableCard key={t.id} t={t} index={i} user={user} onJoin={joinTable} onPay={requireUser(setPaying)} onEdit={setEditing} onDelete={deleteTable}/>)}
      </div>
      {showCreate && <CreateTableModal events={events} onClose={()=>setShowCreate(false)} onCreated={()=>{setShowCreate(false); load();}}/>}
      {editing && <CreateTableModal events={events} table={editing} onClose={()=>setEditing(null)} onCreated={()=>{setEditing(null); load();}}/>}
      {paying && <DepositNoteModal table={paying} onClose={()=>setPaying(null)}/>}
    </div>
  );
}
